import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import rioMark from "@/assets/rio-mark-teal.png.asset.json";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useLanguage();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.location.href = `/#${sectionId}`;
    }
    setIsMenuOpen(false);
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = "5521998000676";
    const message = encodeURIComponent("Hello! I'd like to book a medical consultation with Rio Med Care. Please specify: Online GP (adults) or Online Pediatrics.");
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const navItems = [
    { id: 'pricing', label: t('header.pricing') },
    { id: 'about', label: t('header.about') },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <header className="bg-background/95 backdrop-blur-sm sticky top-0 z-50 border-b border-border">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
              aria-label="Rio Med Care — back to home"
            >
              <img
                src={rioMark.url}
                alt=""
                width={40}
                height={40}
                className="h-9 w-9 object-contain"
              />
              <span className="text-2xl font-bold text-primary tracking-wider">RIO MED CARE</span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-muted-foreground hover:text-foreground transition-colors font-medium text-sm"
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/what-we-treat"
              className="text-muted-foreground hover:text-foreground transition-colors font-medium text-sm"
            >
              What We Treat
            </Link>
          </nav>

          <div className="hidden md:flex items-center">
            <Button 
              variant="default" 
              size="lg"
              onClick={() => scrollToSection('pricing')}
              className="px-8 py-3 font-semibold"
            >
              {t('header.book')}
            </Button>
          </div>

          <button
            className="md:hidden p-3 min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-border">
            <div className="flex flex-col space-y-4 pt-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-foreground hover:text-primary transition-colors py-2"
                >
                  {item.label}
                </button>
              ))}
              <Link
                to="/what-we-treat"
                onClick={() => setIsMenuOpen(false)}
                className="text-left text-foreground hover:text-primary transition-colors py-2"
              >
                What We Treat
              </Link>
              <div className="flex flex-col space-y-2 pt-4">
                <Button variant="ghost" size="sm" onClick={() => window.location.href = `tel:+5521998000676`} className="justify-start">
                  <Phone className="h-4 w-4 mr-2" />
                  Call Now
                </Button>
                <Button variant="default" size="sm" onClick={() => scrollToSection('pricing')} className="justify-start">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t('header.book')}
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
